import { Building2, Banknote } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EstadoBadge } from '../shared/EstadoBadge';
import { formatCurrency } from '@/contabilidad/utils/formatters';
import { FONDO_LABELS } from '@/contabilidad/constants';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import type { DistribucionUtilidad } from '@/contabilidad/types';

interface DetalleDistribucionModalProps {
  distribucion: DistribucionUtilidad | null;
  onClose: () => void;
  onCancelar: (id: number) => Promise<unknown>;
  isCancelando?: boolean;
}

function getInitials(nombre: string): string {
  return nombre
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function DetalleDistribucionModal({ distribucion, onClose, onCancelar, isCancelando = false }: DetalleDistribucionModalProps) {
  const { questionAlert, successAlert, errorAlert } = useAlert();
  const { canDeleteContabilidad } = useAuthorization();

  if (!distribucion) return null;

  const { id, periodo, fondo, fechaReparto, estado, porcentaje, montoARS, montoUSD, detalles, origenes } = distribucion;

  async function handleCancelar() {
    const { isConfirmed } = await questionAlert({
      title: `¿Cancelar el reparto de ${periodo}?`,
      text: 'Se restaurará el saldo disponible para repartir. Quedará constancia de que este reparto fue cancelado.',
      confirmButtonText: 'Sí, cancelar reparto',
    });
    if (!isConfirmed) return;

    try {
      await onCancelar(id);
      successAlert({ title: 'Reparto cancelado', message: 'Se restauró el saldo disponible para repartir.' });
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? undefined;
      errorAlert({ title: 'Error al cancelar', message: msg, exception: msg ? undefined : err });
    }
  }

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Detalle del Reparto</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {/* Resumen */}
          <div className="rounded-lg border bg-slate-50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold">{periodo}</p>
                <p className="text-xs text-muted-foreground">
                  {FONDO_LABELS[fondo]} &middot; Repartido el {fechaReparto} &middot; {porcentaje}% de la utilidad
                </p>
              </div>
              <EstadoBadge estado={estado} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Pesos</p>
                <p className="text-lg font-bold tabular-nums text-gray-900">{formatCurrency(montoARS, 'ARS')}</p>
              </div>
              <div className="text-center">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Dólares</p>
                <p className="text-lg font-bold tabular-nums text-gray-900">{formatCurrency(montoUSD, 'USD')}</p>
              </div>
            </div>
          </div>

          {/* Distribución por Socio */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Distribución por Socio
            </p>
            {detalles && detalles.length > 0 ? (
              <div className="space-y-2">
                {detalles.map(det => (
                  <div key={det.socioId ?? det.socioNombre} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                        {getInitials(det.socioNombre)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold">{det.socioNombre}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums">
                        {formatCurrency(det.montoARS, 'ARS')}
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          {montoARS > 0 ? `(${det.porcentajeARS ?? det.porcentaje ?? 0}%)` : '(—)'}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {formatCurrency(det.montoUSD, 'USD')}
                        <span className="ml-1">
                          {montoUSD > 0 ? `(${det.porcentajeUSD ?? det.porcentaje ?? 0}%)` : '(—)'}
                        </span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">Sin desglose por socio registrado.</p>
            )}
          </div>

          {/* Origen de Fondos */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Origen de Fondos
            </p>
            {origenes && origenes.length > 0 ? (
              <div className="space-y-2">
                {origenes.map(origen => (
                  <div key={`${origen.cajaId}-${origen.moneda}`} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      {origen.cajaTipo === 'banco' ? (
                        <Building2 className="size-4 text-blue-500 shrink-0" />
                      ) : (
                        <Banknote className="size-4 text-slate-500 shrink-0" />
                      )}
                      <span className="text-sm font-medium">{origen.cajaNombre}</span>
                    </div>
                    <span className="text-sm font-semibold tabular-nums">
                      {formatCurrency(origen.monto, origen.moneda)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-2">Sin orígenes registrados.</p>
            )}
          </div>
        </div>

        {canDeleteContabilidad && (
          <DialogFooter>
            {estado === 'Cancelado' ? (
              <p className="text-sm text-muted-foreground">Este reparto fue cancelado.</p>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                disabled={isCancelando}
                onClick={handleCancelar}
              >
                {isCancelando ? 'Cancelando...' : 'Cancelar Reparto'}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
